import type { TFunction } from "i18next";
import { create } from "zustand";

// Undo, as inverse operations rather than as snapshots of the whole segment.
//
// A snapshot would be simpler to record and wrong to replay: between the edit and the undo,
// the worker may have re-run detection, another editor may have touched the same row, and
// retention may have exported it. Restoring a whole segment would silently roll back all of
// that. An inverse says only "put this field back", which is the thing that was actually
// undone.

export interface UndoEntry {
  /**
   * Shown in the toolbar, so "Undo" says what it will do. Already translated: the caller that
   * pushes the entry is the component that knows what it just did.
   */
  label: string;
  run: () => Promise<unknown>;
}

/** Deep enough to cover a run of drags, shallow enough not to hold stale inverses forever. */
export const UNDO_LIMIT = 50;

interface UndoState {
  entries: UndoEntry[];
  busy: boolean;
  error: string | null;
  push: (entry: UndoEntry) => void;
  /** Takes the caller's `t`: a store is not a component and cannot hold a hook. */
  undo: (t: TFunction) => Promise<void>;
  clear: () => void;
}

export const useUndo = create<UndoState>()((set, get) => ({
  entries: [],
  busy: false,
  error: null,

  push: (entry) =>
    set((state) => ({
      entries: [...state.entries, entry].slice(-UNDO_LIMIT),
      error: null,
    })),

  undo: async (t) => {
    const { entries, busy } = get();
    // One at a time. Two undos in flight would race each other's PATCHes and the exclusion
    // constraint would reject whichever lost, leaving the stack claiming work it did not do.
    if (busy) return;
    const entry = entries[entries.length - 1];
    if (!entry) return;

    set({ busy: true, error: null });
    try {
      await entry.run();
      set((state) => ({ entries: state.entries.slice(0, -1), busy: false }));
    } catch (error) {
      // The entry stays on the stack: a failed undo is usually a 409 from an overlapping
      // show, which is worth retrying after the conflicting edit is dealt with.
      set({ busy: false, error: error instanceof Error ? error.message : t("undo.failed") });
    }
  },

  clear: () => set({ entries: [], error: null }),
}));

export function undoLabel(): string | null {
  const entries = useUndo.getState().entries;
  return entries[entries.length - 1]?.label ?? null;
}
